import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — Animates numbers when scrolled into view.
 * Accepts an already-formatted display string (e.g. "17,000+", "10M+")
 * OR a raw integer + suffix. We parse leading digits.
 */
export default function AnimatedCounter({ value, label, accent = "red", testId }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            setStarted(true);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    // Parse leading numeric portion, keep suffix
    const match = String(value).match(/^([\d,\.]+)(.*)$/);
    if (!match) return;
    const numStr = match[1].replace(/,/g, "");
    const suffix = match[2];
    let target = parseFloat(numStr);
    if (isNaN(target)) return;
    const duration = 1800;
    const start = performance.now();
    const format = (n) => {
      // preserve original grouping style: if original had commas add them for integers
      if (target >= 1000 && !numStr.includes(".")) {
        return Math.round(n).toLocaleString("en-IN");
      }
      if (numStr.includes(".")) return n.toFixed(1);
      return Math.round(n).toString();
    };
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(format(target * eased) + suffix);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, value]);

  const colorClass =
    accent === "gold" ? "gold-text" : accent === "green" ? "text-emerald-400" : "red-text";

  return (
    <div
      ref={ref}
      data-testid={testId}
      className="relative glass rounded-3xl p-8 lg:p-10 text-center overflow-hidden group"
    >
      <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-red-500/10 blur-3xl group-hover:bg-red-500/20 transition duration-700" />
      <div className={`font-display font-black text-5xl lg:text-6xl tracking-tighter ${colorClass}`}>
        {display}
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.25em] font-semibold text-slate-400">
        {label}
      </div>
    </div>
  );
}
